import { useState } from 'react';
import { toast } from 'sonner';
import { KeyRound, Link2, Link2Off, Loader2, Plus } from 'lucide-react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { apiClient } from '@/api/client';

// ── Types ─────────────────────────────────────────────────────────────────────

interface LoginMethods {
  has_password: boolean;
  federated: Array<{ provider: 'google' | 'microsoft'; email: string; linked_at: string }>;
}

// ── Data hooks ────────────────────────────────────────────────────────────────

function useLoginMethods() {
  return useQuery({
    queryKey: ['portal-login-methods'],
    queryFn: async () => {
      const { data } = await apiClient.get('/portal/auth/methods');
      return data.data as LoginMethods;
    },
    staleTime: 30_000,
  });
}

function useInitOAuth() {
  return useMutation({
    mutationFn: async ({ provider, flow }: { provider: string; flow: string }) => {
      const { data } = await apiClient.post(`/portal/auth/oauth/${provider}/init`, { flow });
      return data.data as { authorization_url: string };
    },
  });
}

function useRemovePassword() {
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete('/portal/auth/methods/password');
    },
  });
}

function useSetPassword() {
  return useMutation({
    mutationFn: async (body: { new_password: string; current_password?: string }) => {
      await apiClient.post('/portal/auth/methods/password', body);
    },
  });
}

function useUnlinkFederated() {
  return useMutation({
    mutationFn: async (provider: string) => {
      await apiClient.delete(`/portal/auth/methods/federated/${provider}`);
    },
  });
}

// ── Password section ──────────────────────────────────────────────────────────

function PasswordSection({ hasPassword, onRefetch }: { hasPassword: boolean; onRefetch: () => void }) {
  const setPasswordMut = useSetPassword();
  const removePasswordMut = useRemovePassword();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    if (next.length < 10) { setError('Password must be at least 10 characters'); return; }
    if (next !== confirm) { setError('Passwords do not match'); return; }
    try {
      await setPasswordMut.mutateAsync({
        new_password: next,
        ...(hasPassword ? { current_password: current } : {}),
      });
      toast.success(hasPassword ? 'Password updated' : 'Password added');
      setCurrent(''); setNext(''); setConfirm('');
      setShowForm(false);
      onRefetch();
    } catch (err: any) {
      const code = err?.response?.data?.errors?.code;
      const msg = err?.response?.data?.errors?.message;
      if (code === 'INVALID_PASSWORD') {
        setError('Current password is incorrect');
        return;
      }
      setError(msg ?? 'Failed to update password');
    }
  }

  async function handleRemove() {
    setError('');
    try {
      await removePasswordMut.mutateAsync();
      toast.success('Password removed');
      onRefetch();
    } catch (err: any) {
      const code = err?.response?.data?.errors?.code;
      if (code === 'LAST_LOGIN_METHOD') {
        setError('Cannot remove your only login method. Add another method first.');
        return;
      }
      setError('Failed to remove password');
    }
  }

  return (
    <div className="rounded-lg border border-border-subtle bg-bg-surface p-5 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-text-secondary" />
          <h2 className="font-semibold text-text-primary">Password</h2>
        </div>
        {hasPassword ? (
          <span className="text-xs text-text-secondary px-2 py-0.5 rounded-full border border-border-subtle">Set</span>
        ) : (
          <span className="text-xs text-text-tertiary">Not set</span>
        )}
      </div>

      {!showForm ? (
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowForm(true)}>
            {hasPassword ? 'Change password' : 'Add password'}
          </Button>
          {hasPassword && (
            <Button
              variant="ghost"
              size="sm"
              className="text-accent-rose hover:text-accent-rose"
              disabled={removePasswordMut.isPending}
              onClick={handleRemove}
            >
              {removePasswordMut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Remove'}
            </Button>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-3 max-w-sm">
          {hasPassword && (
            <div className="space-y-1.5">
              <Label htmlFor="lm-current">Current password</Label>
              <Input
                id="lm-current"
                type="password"
                autoComplete="current-password"
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
              />
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="lm-new">New password</Label>
            <Input
              id="lm-new"
              type="password"
              autoComplete="new-password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="lm-confirm">Confirm password</Label>
            <Input
              id="lm-confirm"
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          {error && (
            <p className="text-sm text-accent-rose" data-testid="lm-error">{error}</p>
          )}
          <div className="flex gap-2">
            <Button type="submit" size="sm" disabled={setPasswordMut.isPending || !next || !confirm || (hasPassword && !current)}>
              {setPasswordMut.isPending ? 'Saving…' : (hasPassword ? 'Update password' : 'Add password')}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => { setShowForm(false); setError(''); }}>
              Cancel
            </Button>
          </div>
        </form>
      )}

      {!showForm && error && (
        <p className="text-sm text-accent-rose" data-testid="lm-error">{error}</p>
      )}
    </div>
  );
}

// ── Federated accounts section ────────────────────────────────────────────────

const PROVIDER_LABELS: Record<string, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
};

const UNLINKED_PROVIDERS = ['google', 'microsoft'] as const;

function FederatedSection({ federated, onRefetch }: { federated: LoginMethods['federated']; onRefetch: () => void }) {
  const initOAuth = useInitOAuth();
  const unlinkMut = useUnlinkFederated();
  const [errors, setErrors] = useState<Record<string, string>>({});

  async function handleLink(provider: string) {
    try {
      const { authorization_url } = await initOAuth.mutateAsync({ provider, flow: 'link' });
      window.location.href = authorization_url;
    } catch (err: any) {
      const msg = err?.response?.data?.errors?.message ?? 'Failed to start linking';
      toast.error(msg);
    }
  }

  async function handleUnlink(provider: string) {
    setErrors((e) => ({ ...e, [provider]: '' }));
    try {
      await unlinkMut.mutateAsync(provider);
      toast.success(`${PROVIDER_LABELS[provider] ?? provider} unlinked`);
      onRefetch();
    } catch (err: any) {
      const code = err?.response?.data?.errors?.code;
      if (code === 'LAST_LOGIN_METHOD') {
        setErrors((e) => ({ ...e, [provider]: 'Cannot remove your only login method. Add another method first.' }));
        return;
      }
      toast.error('Failed to unlink account');
    }
  }

  const linkedProviders = new Set(federated.map((f) => f.provider));

  return (
    <div className="rounded-lg border border-border-subtle bg-bg-surface p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Link2 className="h-4 w-4 text-text-secondary" />
        <h2 className="font-semibold text-text-primary">Connected accounts</h2>
      </div>

      <div className="space-y-3">
        {UNLINKED_PROVIDERS.map((provider) => {
          const linked = federated.find((f) => f.provider === provider);
          const label = PROVIDER_LABELS[provider];
          return (
            <div key={provider}>
              <div className="flex items-center justify-between py-2 border-b border-border-subtle last:border-0">
                <div className="flex items-center gap-3">
                  <ProviderIcon provider={provider} />
                  <div>
                    <p className="text-sm font-medium text-text-primary">{label}</p>
                    {linked ? (
                      <p className="text-xs text-text-secondary">{linked.email}</p>
                    ) : (
                      <p className="text-xs text-text-tertiary">Not connected</p>
                    )}
                  </div>
                </div>
                {linked ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-accent-rose hover:text-accent-rose gap-1.5"
                    disabled={unlinkMut.isPending && linkedProviders.has(provider)}
                    onClick={() => handleUnlink(provider)}
                  >
                    <Link2Off className="h-3.5 w-3.5" />
                    Disconnect
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    disabled={initOAuth.isPending}
                    onClick={() => handleLink(provider)}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Connect
                  </Button>
                )}
              </div>
              {errors[provider] && (
                <p className="text-sm text-accent-rose mt-1" data-testid={`lm-error-${provider}`}>
                  {errors[provider]}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProviderIcon({ provider }: { provider: string }) {
  if (provider === 'google') {
    return (
      <div className="flex h-7 w-7 items-center justify-center rounded border border-border-subtle bg-bg-elevated text-xs font-bold text-text-secondary">
        G
      </div>
    );
  }
  return (
    <div className="flex h-7 w-7 items-center justify-center rounded border border-border-subtle bg-bg-elevated text-xs font-bold text-text-secondary">
      M
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export function PortalSettingsLoginMethodsPage() {
  const { data: methods, isLoading, refetch } = useLoginMethods();

  if (isLoading) {
    return (
      <div className="space-y-4 max-w-lg">
        <Skeleton className="h-32 w-full rounded-lg" />
        <Skeleton className="h-32 w-full rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-lg">
      <div className="flex items-center gap-2">
        <KeyRound className="h-5 w-5 text-text-secondary" />
        <h1 className="text-lg font-bold text-text-primary">Login methods</h1>
      </div>

      <PasswordSection
        hasPassword={methods?.has_password ?? false}
        onRefetch={() => refetch()}
      />

      <FederatedSection
        federated={methods?.federated ?? []}
        onRefetch={() => refetch()}
      />
    </div>
  );
}
